import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronRight } from "lucide-react";
import { SITE } from "@/data/site";
import { KEY_SERVICES, TUNING_SERVICES } from "@/data/services";
import Logo from "@/components/Logo";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const NavCol = ({ title, items }) => (
    <div>
      <p className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt mb-3">
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((s) => (
          <li key={s.slug}>
            <Link
              to={`/${s.slug}`}
              data-testid={`nav-service-${s.slug}`}
              className="group flex items-center gap-2 py-1.5 text-neutral-300 hover:text-white transition-colors"
            >
              <ChevronRight className="h-3.5 w-3.5 text-neutral-600 group-hover:text-volt transition-colors" />
              <span className="font-body text-sm">{s.nav}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex h-[72px] items-center justify-between">
            <Link
              to="/"
              data-testid="header-logo"
              className="flex items-center gap-3"
            >
              <Logo />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              <div className="group relative">
                <button
                  data-testid="nav-leistungen-trigger"
                  className="font-body text-sm font-semibold text-neutral-200 hover:text-volt transition-colors py-2"
                >
                  Leistungen
                </button>
                <div className="invisible absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="grid grid-cols-2 gap-8 rounded-sm border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl p-7">
                    <NavCol title="Tuning & Elektronik" items={TUNING_SERVICES} />
                    <NavCol title="Schlüssel & Notdienst" items={KEY_SERVICES} />
                  </div>
                </div>
              </div>
              <Link to="/konfigurator" data-testid="nav-konfigurator" className="font-body text-sm font-semibold text-neutral-200 hover:text-volt transition-colors">
                Konfigurator
              </Link>
              <Link to="/#schnellanfrage" data-testid="nav-schnellanfrage" onClick={(e)=>{e.preventDefault();navigate("/");setTimeout(()=>document.getElementById("schnellanfrage")?.scrollIntoView({behavior:"smooth"}),100);}} className="font-body text-sm font-semibold text-volt hover:text-white transition-colors">
                60-Sek-Anfrage
              </Link>
              <Link to="/#ablauf" onClick={(e)=>{e.preventDefault();navigate("/");setTimeout(()=>document.getElementById("ablauf")?.scrollIntoView({behavior:"smooth"}),100);}} className="font-body text-sm font-semibold text-neutral-200 hover:text-volt transition-colors">
                Ablauf
              </Link>
              <Link to="/#kontakt" data-testid="nav-kontakt" onClick={(e)=>{e.preventDefault();navigate("/");setTimeout(()=>document.getElementById("kontakt")?.scrollIntoView({behavior:"smooth"}),100);}} className="font-body text-sm font-semibold text-neutral-200 hover:text-volt transition-colors">
                Kontakt
              </Link>
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a
                href={`tel:${SITE.phoneIntl}`}
                data-testid="header-call-btn"
                className="group inline-flex items-center gap-2 bg-volt px-5 py-2.5 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] glow-volt"
              >
                <Phone className="h-4 w-4" />
                {SITE.phoneDisplay}
              </a>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              aria-label="Menü"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-xl pt-[72px] lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-8">
              <NavCol title="Tuning & Elektronik" items={TUNING_SERVICES} />
              <NavCol title="Schlüssel & Notdienst" items={KEY_SERVICES} />
              <Link
                to="/konfigurator"
                data-testid="mobile-nav-konfigurator"
                className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 font-body text-sm font-bold uppercase text-white"
              >
                Konfigurator
              </Link>
              <button
                type="button"
                data-testid="mobile-schnellanfrage-btn"
                onClick={() => { navigate("/"); setTimeout(() => document.getElementById("schnellanfrage")?.scrollIntoView({ behavior: "smooth" }), 150); }}
                className="flex w-full items-center justify-center gap-2 border border-volt px-5 py-3.5 font-body text-sm font-bold uppercase text-volt"
              >
                60-Sekunden-Anfrage
              </button>
              <a
                href={`tel:${SITE.phoneIntl}`}
                data-testid="mobile-call-btn"
                className="flex items-center justify-center gap-2 bg-volt px-5 py-4 font-body text-base font-bold uppercase text-black w-full"
              >
                <Phone className="h-5 w-5" /> Jetzt anrufen
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
